import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
    useTheme,
    useMediaQuery,
    Box,
    Paper,
    Typography,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    Collapse,
    ListItemButton
} from '@mui/material';
import { fetchLegMembersByFlt } from 'state/api';
import { hasPermission } from 'state/checkPermission';
import FlexBetween from 'components/FlexBetween';
import CrewListDialog from 'components/CrewListDialog';


const SectorList = ({ 
    flightsList,
    selectedSector,
    setSelectedSector,
}) => {
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const backgroundAlt = theme.palette.background.alt;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const canView = hasPermission(user?.permissions, "FlightMap", "view", false);
    
    const scrollRef = useRef(null);
    const listItemRefs = useRef(new Map());
 
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [dialogState, setDialogState] = useState({
        open: false,
        flight: [],
        crewList: [],
        jumpList: []
    });

    const handleSectorClick = useCallback((sector) => {
        setSelectedSector(prev => (prev === sector ? null : sector));
    }, [setSelectedSector]);

    const handleCloseCrewListDialog = () => {
        setDialogState(prev => ({ ...prev, open: false }));
    };

    const setListItemRef = useCallback(
        (sector) => (element) => {
            if (element) listItemRefs.current.set(sector, element);
            else listItemRefs.current.delete(sector);
        },
        []
    );

    const handleViewClick = useCallback(async (flightDate, flightDesc, dep) => {
        try {
            const { flightDetails, legMembers, jumpSeaters } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);            
            setDialogState({
                open: true,
                flight: flightDetails,
                crewList: legMembers,
                jumpList: jumpSeaters
            });
        } catch (error) {
            setDialogState(prev => ({ ...prev, open: false }));
        }
    }, []);

    useEffect(() => {
        if (selectedSector && scrollRef.current && listItemRefs.current.has(selectedSector)) {
            const listItemElement = listItemRefs.current.get(selectedSector);
            const offsetTop = listItemElement.offsetTop;
            const container = scrollRef.current;
            const containerHeight = container.clientHeight;
            const elementHeight = listItemElement.offsetHeight;
            const scrollTop = offsetTop - (containerHeight - elementHeight) / 2;
            container.scrollTop = scrollTop;
        }
    }, [selectedSector]);

    return (
        <Box
            sx={{
                height: '100%',
                overflow: 'auto',
            }}
            ref={scrollRef}
        >
            <Paper
                elevation={2}
                sx={{
                    minHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                }}
            >
                <FlexBetween
                    zIndex={9}
                    position="sticky"
                    top={0}
                    p={2}
                    backgroundColor={backgroundAlt}
                    borderBottom={`1px solid ${theme.palette.neutral.deep}`}
                >
                    <Typography variant="h5" color={theme.palette.primary.main}>
                        Sector List
                    </Typography>
                    {selectedSector && (
                        <Button variant="outlined" size="small" onClick={() => setSelectedSector(null)}>
                            Show All
                        </Button>
                    )}
                </FlexBetween>

                <Divider />

                <List dense sx={{ flexGrow: 1 }}>
                    {flightsList.map((flight, index) => {
                        const isSelected = selectedSector === flight.sector;
                        return (
                        <React.Fragment key={flight.sector}>
                            <ListItemButton
                                ref={setListItemRef(flight.sector)}
                                selected={isSelected}
                                onClick={() => handleSectorClick(flight.sector)}
                                sx={{
                                    borderLeft: isSelected
                                        ? `2px solid ${theme.palette.primary.main}`
                                        : '2px solid transparent',
                                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                    '&.Mui-selected': {
                                        bgcolor: `${theme.palette.primary.main}18`,
                                        '&:hover': { bgcolor: `${theme.palette.primary.main}28` },
                                    },
                                    '&:hover': { bgcolor: theme.palette.action.hover },
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={isSelected ? 'bold' : 'normal'}
                                                color={isSelected ? 'primary.main' : 'text.primary'}
                                            >
                                                {`${index + 1}. ${flight.sector} (${flight.count} flights)`}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItemButton>
                            <Collapse in={selectedSector === flight.sector} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding dense>
                                    {flight.flights.map((f, index) => {
                                        const flightKey = f._id || `${flight.sector}-${index}`;
                                        const isFlightSelected = selectedFlight === flightKey;
                                        return (
                                        <ListItem
                                            key={`${flight.sector}-${index}`}
                                            sx={{
                                                pl: 4,
                                                cursor: canView ? 'pointer' : 'default',
                                                '&:hover .list-item-primary': {
                                                    color: 'primary.dark',
                                                },
                                                '& .list-item-primary': {
                                                    transition: 'all 0.3s ease',
                                                },
                                            }}
                                            onClick={() => {
                                                if (!canView) return;
                                                setSelectedFlight(flightKey);
                                                handleViewClick(f.flightDate, f.flightDesc, f.dep);
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        className="list-item-primary"
                                                        sx={isFlightSelected ? {
                                                            textDecoration: 'underline',
                                                            color: 'primary.main',
                                                        } : undefined}
                                                    >
                                                        {`${index + 1}. ${f.flightDesc || f.flightNo}`}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <>
                                                        <Typography variant="caption" display="block">
                                                            Date:{' '}
                                                            {timeMode === 'UTC' ? (
                                                                <>
                                                                    {f.flightDate ? format(new Date(f.flightDate), 'dd/MM/yyyy') : 'N/A'}{' '}
                                                                    {f.etd} - {f.eta}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {f.flightDateLocalBase
                                                                        ? format(new Date(f.flightDateLocalBase), 'dd/MM/yyyy')
                                                                        : 'N/A'}{' '}
                                                                    {f.etdLocalBase} - {f.etaLocalBase}
                                                                </>
                                                            )}
                                                        </Typography>
                                                        <Typography variant="caption" display="block">
                                                            Aircraft: {f.acType} ({f.flightReg})
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    );
                })}
                </List>
            </Paper>

            {dialogState.open && 
                <CrewListDialog 
                    open={dialogState.open}
                    onClose={handleCloseCrewListDialog}
                    flight={dialogState.flight}
                    crewList={dialogState.crewList}
                    jumpList={dialogState.jumpList}
                />
            }
        </Box>
    );
};

const areEqual = (prevProps, nextProps) => {
    return (
        prevProps.selectedSector === nextProps.selectedSector &&
        prevProps.flightsList === nextProps.flightsList
    );
};

export default React.memo(SectorList, areEqual);



